import { useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useGetDocumentReadinessSummaryQuery } from '@/apis/extended/documentRequirementsApi';
import {
  useGetLoanApplicationDocumentsQuery,
  usePatchLoanChecklistMutation,
  useUploadLoanApplicationDocumentMutation,
} from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { UploadedDocumentsList } from '@/components/Profile/UploadedDocumentsList';
import {
  collectUploadedDocumentsForDisplay,
  documentDescriptionForDisplayLabel,
  mergeLoanChecklistWithProfileDocuments,
} from '@/utils/document';
import type {
  LoanApplication,
  LoanChecklistItem,
} from '@/types/LoanApplicationType';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
  /** Restrict the displayed rows to a single underwriting-style category.
   *  When provided, only checklist items whose `key` matches the filter or
   *  whose label/description includes the filter substring are rendered. */
  categoryFilter?: string;
};

export function ChecklistSection({
  application,
  readOnly,
  categoryFilter,
}: Props) {
  const { data: user } = useGetCurrentUserQuery();
  const { data: docReadiness } = useGetDocumentReadinessSummaryQuery(
    undefined,
    { skip: !user }
  );
  const { data: docs = [], refetch: refetchDocs } =
    useGetLoanApplicationDocumentsQuery(application._id);
  const [patch, { isLoading: patchLoading }] = usePatchLoanChecklistMutation();
  const [upload, { isLoading: uploadLoading }] =
    useUploadLoanApplicationDocumentMutation();
  const fileRef = useRef<HTMLInputElement>(null);
  const pendingItemId = useRef<string | null>(null);

  const checklistRows = useMemo(() => {
    const rows = mergeLoanChecklistWithProfileDocuments(
      user ?? null,
      application,
      docReadiness
    );
    if (!categoryFilter) return rows;
    const needle = categoryFilter.toLowerCase();
    return rows.filter((r) => {
      const haystack = `${r.key} ${r.label}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [user, application, docReadiness, categoryFilter]);

  const uploadedDocuments = useMemo(
    () =>
      collectUploadedDocumentsForDisplay({
        user,
        docReadiness,
        loanDocs: docs,
        checklistRows,
      }),
    [user, docReadiness, docs, checklistRows]
  );

  const toggle = async (item: LoanChecklistItem) => {
    if (readOnly) return;
    const next = checklistRows.map((c) =>
      c.key === item.key
        ? {
            ...c,
            completed: !c.completed,
            completedAt: !c.completed ? new Date().toISOString() : undefined,
          }
        : c
    );
    try {
      await patch({ id: application._id, checklist: next }).unwrap();
    } catch {
      toast.error('Update failed');
    }
  };

  const pickFile = (checklistItemId: string) => {
    pendingItemId.current = checklistItemId;
    fileRef.current?.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    const cid = pendingItemId.current;
    e.target.value = '';
    if (!f || !cid) return;
    try {
      await upload({
        applicationId: application._id,
        file: f,
        checklistItemId: cid,
        title: f.name,
      }).unwrap();
      refetchDocs();
      toast.success('Uploaded');
    } catch {
      toast.error('Upload failed');
    }
  };

  return (
    <div>
      <input ref={fileRef} type="file" hidden onChange={onFile} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Required items come from your loan type, program, and occupancy. Uploads
        from Loan → Documents or here update the same checklist.
      </Typography>
      <List dense>
        {checklistRows.map((item) => (
          <ListItem key={item.key} divider>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={item.completed}
                disabled={readOnly || patchLoading}
                onChange={() => toggle(item)}
              />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={`${documentDescriptionForDisplayLabel(item.label)} · ${
                item.required ? 'Required' : 'Optional'
              }`}
            />
            {!readOnly && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  onClick={() => pickFile(item._id ?? item.key)}
                >
                  Upload
                </Button>
              </Box>
            )}
          </ListItem>
        ))}
      </List>
      <UploadedDocumentsList uploads={uploadedDocuments} />
      {uploadLoading && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Uploading…
        </Typography>
      )}
    </div>
  );
}
